import { termMaxConfigByChain } from '@1delta/data-sdk'
import { createTermMaxDataSource } from './apiClient'
import { getTermMaxMaturedResidue } from './maturedRoster'
import type {
  TermMaxDataSource,
  TermMaxMarketConfig,
  TermMaxMarketRaw,
} from './types'

/**
 * Last-known-good snapshot cache, keyed by chainId.
 *
 * The TermMax API is an undocumented app backend with no rate-limit statement.
 * A raw failure would collapse every market on the chain to zero rates AND drop
 * the whole market list for the entire downstream meta-cache window (~5 min at
 * the edge) — blanking the public rate display and, worse, making the write
 * path unable to resolve a market. Reusing the last good snapshot keeps a
 * slightly stale but representative view flowing until the API recovers.
 *
 * Safe because the meta rates are DISPLAY-ONLY: any actual borrow/lend prices
 * off a live quote at build time, never off this snapshot. The window is
 * bounded so a genuinely dead chain eventually falls back to empty.
 */
const LKG_TTL_SEC = 30 * 60
const lastGood = new Map<string, { markets: TermMaxMarketRaw[]; at: number }>()

/**
 * Stale-while-revalidate window over the same snapshot.
 *
 * `/market/data` is AWAITED on the hot path and was measured at 7.5s, 1.1s,
 * 0.3s on consecutive calls — an inline await makes the whole chain's user
 * read pay the API's worst tail. Within this window the snapshot is served
 * as-is; past it (but inside {@link LKG_TTL_SEC}) it is served IMMEDIATELY
 * while a single-flight background refresh replaces it for the next caller.
 * Only a COLD cache still awaits the API — returning empty there would blank
 * every TermMax market and break write-path market resolution, which is the
 * one thing the matured-residue cache (a pure additive) is allowed to do and
 * this one is not.
 */
const SWR_FRESH_MS = 60_000
/** Floor between refresh ATTEMPTS, so a failing API is not hammered per read. */
const REFRESH_RETRY_MS = 30_000
const lastRefreshAttempt = new Map<string, number>()
/** In-flight refreshes, so concurrent reads do not stampede the API. */
const refreshInFlight = new Map<string, Promise<void>>()

/**
 * Market-resolution cache, keyed `${chainId}:${marketAddrLower}`.
 *
 * TermMax markets are discovered DYNAMICALLY (there is no static registry —
 * they churn on every maturity roll), but the write path needs to resolve a
 * `TERMMAX_<ADDR>` lender key back to its FT/XT/GT addresses and maturity. Every
 * public fetch refreshes this, and `getCachedTermMaxMarket` reads it — the same
 * trick the Teller/Liquity discovery caches use to hand a later phase a layout
 * it did not fetch itself.
 */
const MARKET_CACHE_TTL_MS = 10 * 60_000
const marketCache = new Map<
  string,
  { config: TermMaxMarketConfig; at: number }
>()

const marketKey = (chainId: string | number, market: string) =>
  `${chainId}:${market.toLowerCase()}`

function rememberMarkets(chainId: string, raw: TermMaxMarketRaw[]): void {
  const at = Date.now()
  for (const { config } of raw) {
    marketCache.set(marketKey(chainId, config.market), { config, at })
  }
}

/**
 * Resolve a TermMax market from the discovery cache, or undefined when it was
 * never fetched / has gone stale. Callers that need a guaranteed answer should
 * `await fetchTermMaxMarkets(chainId)` first (or read the market on-chain).
 */
export function getCachedTermMaxMarket(
  chainId: string | number,
  market: string,
): TermMaxMarketConfig | undefined {
  const hit = marketCache.get(marketKey(chainId, market))
  if (!hit || Date.now() - hit.at > MARKET_CACHE_TTL_MS) return undefined
  return hit.config
}

/** All markets cached for a chain (may be empty before the first fetch). */
export function getCachedTermMaxMarkets(
  chainId: string | number,
): TermMaxMarketConfig[] {
  const prefix = `${chainId}:`
  const now = Date.now()
  const out: TermMaxMarketConfig[] = []
  for (const [key, hit] of marketCache) {
    if (!key.startsWith(prefix)) continue
    if (now - hit.at > MARKET_CACHE_TTL_MS) continue
    out.push(hit.config)
  }
  return out
}

/** Test seam: drop both caches. */
export function __resetTermMaxCaches(): void {
  lastGood.clear()
  marketCache.clear()
  lastRefreshAttempt.clear()
  refreshInFlight.clear()
}

/**
 * Fetch `/market/data` and replace the snapshot on success. Single-flight per
 * chain; a failure NEVER overwrites a good snapshot (the LKG contract) and
 * only delays the next attempt via {@link REFRESH_RETRY_MS}.
 */
function refreshLiveMarkets(
  chainId: string,
  source: TermMaxDataSource,
): Promise<void> {
  const running = refreshInFlight.get(chainId)
  if (running) return running
  lastRefreshAttempt.set(chainId, Date.now())
  const p = source
    .getChainMarkets(chainId)
    .then((fresh) => {
      if (!fresh) return
      const nowSec = Math.floor(Date.now() / 1000)
      const live = fresh.filter((m) => m.config.maturity > nowSec)
      lastGood.set(chainId, { markets: live, at: nowSec })
      rememberMarkets(chainId, live)
    })
    .catch(() => undefined)
    .finally(() => refreshInFlight.delete(chainId))
  refreshInFlight.set(chainId, p)
  return p
}

/**
 * Fetch every live TermMax market on a chain, with its order book collapsed to
 * a best-rate + depth snapshot.
 *
 * Returns `[]` (not an error) when the chain has no TermMax deployment
 * configured, so callers can fan out across chains unconditionally.
 *
 * Markets are DISCOVERED, never read from a static list: matured markets vanish
 * from upstream entirely rather than lingering with a flag, and ~15% of the
 * book can roll on a single maturity date.
 */
export async function fetchTermMaxMarkets(
  chainId: string,
  source: TermMaxDataSource = createTermMaxDataSource(chainId),
  options?: { includeMatured?: boolean },
): Promise<TermMaxMarketRaw[]> {
  if (!termMaxConfigByChain(chainId)) return []

  const nowSec = Math.floor(Date.now() / 1000)
  const cached = lastGood.get(chainId)
  const ageSec = cached ? nowSec - cached.at : Infinity

  let live: TermMaxMarketRaw[]
  if (cached && ageSec <= LKG_TTL_SEC) {
    // Serve the snapshot; refresh BEHIND the request once it leaves the fresh
    // window (single-flight, rate-floored) so this call never pays the API's
    // multi-second tail. Drop anything that matured since the snapshot.
    live = cached.markets.filter((m) => m.config.maturity > nowSec)
    const attemptedAt = lastRefreshAttempt.get(chainId) ?? 0
    if (
      ageSec * 1000 > SWR_FRESH_MS &&
      Date.now() - attemptedAt > REFRESH_RETRY_MS
    ) {
      void refreshLiveMarkets(chainId, source)
    }
  } else {
    // Cold or expired: the ONE case that must still await the API — an empty
    // answer here would blank the chain's whole book and break write-path
    // market resolution.
    await refreshLiveMarkets(chainId, source)
    const after = lastGood.get(chainId)
    if (!after || nowSec - after.at > LKG_TTL_SEC) return []
    live = after.markets.filter((m) => m.config.maturity > nowSec)
  }

  const markets = options?.includeMatured
    ? [...live, ...maturedRows(chainId, live)]
    : live

  rememberMarkets(chainId, markets)
  return markets
}

/**
 * Matured markets, as rows the public converter can render.
 *
 * WHY THE PUBLIC SURFACE WANTS THEM AT ALL. A matured market cannot be entered,
 * so it has nothing to advertise — but a user can still be IN one, and the
 * user-data converter renders a position against the market's meta row. No row,
 * no position: someone who never redeemed their FT is invisible. Measured
 * 2026-08-21, that is ~1.24M USDC unredeemed across 306 matured Ethereum
 * markets, including a single ~998k position eight months past maturity.
 *
 * WHY THIS IS SAFE WITHOUT AN EXCLUSION LIST. `convertTermMaxMarketsToResponse`
 * already branches on maturity: rates and liquidity are forced to 0 and
 * `depositsEnabled` / `borrowingEnabled` are `!matured`. Those flags ARE the
 * exclusion mechanism, and every consumer already honours them — a separate
 * address list would be a second place to keep the same fact, and the two would
 * drift.
 *
 * There are no orders for a matured market (nothing can quote against it), so
 * the rows carry an empty book, which is what the converter's matured branch
 * expects.
 */
function maturedRows(
  chainId: string,
  live: TermMaxMarketRaw[],
): TermMaxMarketRaw[] {
  const seen = new Set(live.map((m) => m.config.market.toLowerCase()))
  // Cached-or-empty, never awaited — see `getTermMaxMaturedResidue`.
  const residue = getTermMaxMaturedResidue(chainId)
  return residue
    .filter(
      (config: TermMaxMarketConfig) => !seen.has(config.market.toLowerCase()),
    )
    .map((config: TermMaxMarketConfig) => ({ config, top: null, orders: [] }))
}
